import { type PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useRange, type UseRangeProps } from 'react-instantsearch';

import { Dropdown } from '~/apps/product-plus/components/cxy/cxy.ts';

import styles from './styles/RangeSelectFilter.module.css';

const generateOptions = (min: number, max: number) => {
  const options = [];
  for (let i = min; i <= max; i++) {
    options.push({ label: i.toString(), value: i.toString() });
  }
  return options;
};

export type RangeSelectFilterProps = PropsWithChildren<UseRangeProps & {isDisabled?: boolean;label?: string;}>;

export const RangeSelectFilter = ({ isDisabled = false, label, ...rangeProps }: RangeSelectFilterProps) => {
  const { canRefine, range, refine, start } = useRange(rangeProps);
  const [minValue, setMinValue] = useState(range.min);
  const [maxValue, setMaxValue] = useState(range.max);
  const hasEverBeenRefinable = useRef(false);

  const from = Number.isFinite(start[0]) ? start[0] : range.min;
  const to = Number.isFinite(start[1]) ? start[1] : range.max;

  const options = generateOptions(range.min ?? 0, range.max ?? 0);

  if (canRefine && !hasEverBeenRefinable.current) {
    hasEverBeenRefinable.current = true;
  }

  const handleValueChange = (value: string, type: 'min' | 'max') => {
    if (minValue === undefined || maxValue === undefined) return;

    const newValue = parseInt(value, 10);
    const [adjustedMin, adjustedMax] =
    type === 'min' ? [newValue, Math.max(newValue, maxValue)] : [Math.min(newValue, minValue), newValue];

    refine([adjustedMin, adjustedMax]);
  };

  useEffect(() => {
    setMinValue(from);
    setMaxValue(to);
  }, [from, to]);

  const shouldRender = hasEverBeenRefinable.current && typeof minValue === 'number' && typeof maxValue === 'number';

  if (!shouldRender) return null;

  return (
    <div className={styles.rangeSelectFilterContainer}>
      <span className={styles.rangeSelectFilterLabel}>{label}</span>
      <div className={styles.rangeSelectFilterItems}>
        <div className={styles.rangeSelectFilterItem}>
          <Dropdown
            disabled={isDisabled || !canRefine}
            hiddenLabel
            label={`${label} min value`}
            maxItemsVisible={5}
            onValueChange={(value) => handleValueChange(value, 'min')}
            options={options}
            placeholder="Min"
            value={minValue.toString()} />

        </div>
        <div className={styles.rangeSelectFilterItem}>
          <Dropdown
            disabled={isDisabled || !canRefine}
            hiddenLabel
            label={`${label} max value`}
            maxItemsVisible={5}
            onValueChange={(value) => handleValueChange(value, 'max')}
            options={options}
            placeholder="Max"
            value={maxValue.toString()} />

        </div>
      </div>
    </div>);

};